import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Circle, Rocket, X, CaretRight } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from '@/contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const checklistItems = [
  {
    id: 'first_seo_page',
    title: 'Créer votre première page SEO',
    description: 'Lancez votre acquisition client',
    path: '/seo-generator',
    cta: 'Créer une page',
  },
  {
    id: 'ten_seo_pages',
    title: 'Générer 10 pages SEO',
    description: 'Multipliez vos chances d\'être trouvé',
    path: '/seo-pages',
    cta: 'Voir mes pages',
  },
  {
    id: 'first_keyword',
    title: 'Ajouter un mot-clé à suivre',
    description: 'Surveillez votre positionnement Google',
    path: '/ranking',
    cta: 'Ajouter un mot-clé',
  },
  {
    id: 'view_dashboard',
    title: 'Consulter le dashboard SEO',
    description: 'Analysez vos performances',
    path: '/stats',
    cta: 'Voir les stats',
  },
];

export default function OnboardingChecklist() {
  const { isDark } = useTheme();
  const [progress, setProgress] = useState({
    first_seo_page: false,
    ten_seo_pages: false,
    first_keyword: false,
    view_dashboard: false,
  });
  const [isExpanded, setIsExpanded] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/onboarding/progress`, {
        withCredentials: true,
      });
      setProgress(response.data.progress);
      setIsComplete(response.data.is_complete);
      
      // Si pas complet, garder ouvert
      if (!response.data.is_complete) {
        setIsExpanded(true);
      }
    } catch (error) {
      console.error('Error fetching onboarding progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsComplete = async (itemId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/onboarding/complete`,
        { item_id: itemId },
        { withCredentials: true }
      );
      
      setProgress(prev => {
        const updated = { ...prev, [itemId]: true };
        const allComplete = Object.values(updated).every(v => v);
        setIsComplete(allComplete);
        return updated;
      });
    } catch (error) {
      console.error('Error marking item complete:', error);
    }
  };

  const completedCount = Object.values(progress).filter(Boolean).length;
  const totalCount = checklistItems.length;
  const progressPercent = Math.round((completedCount / totalCount) * 100);

  // Ne pas afficher si tout est complet et fermé
  if (isComplete && !isExpanded) {
    return null;
  }

  if (loading) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl overflow-hidden ${
        isDark 
          ? 'bg-gradient-to-br from-violet-500/10 to-pink-500/5 border border-violet-500/20' 
          : 'bg-gradient-to-br from-violet-50 to-pink-50 border border-violet-200'
      }`}
    >
      {/* Header */}
      <div 
        className={`p-4 flex items-center justify-between cursor-pointer ${
          isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-white/50'
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
            isComplete 
              ? 'bg-emerald-500' 
              : 'bg-gradient-to-br from-violet-500 to-pink-500'
          }`}>
            {isComplete ? (
              <Check size={20} weight="bold" className="text-white" />
            ) : (
              <Rocket size={20} weight="fill" className="text-white" />
            )}
          </div>
          <div>
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {isComplete ? 'Onboarding terminé !' : 'Démarrage rapide'}
            </h3>
            <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {isComplete ? 'Bravo, vous êtes prêt !' : `${completedCount}/${totalCount} étapes complétées`}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Progress Circle */}
          <div className="relative w-10 h-10">
            <svg className="w-10 h-10 -rotate-90">
              <circle
                cx="20"
                cy="20"
                r="16"
                fill="none"
                stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
                strokeWidth="3"
              />
              <circle
                cx="20"
                cy="20"
                r="16"
                fill="none"
                stroke="url(#progressGradient)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${progressPercent} 100`}
              />
              <defs>
                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#8b5cf6" />
                  <stop offset="100%" stopColor="#ec4899" />
                </linearGradient>
              </defs>
            </svg>
            <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {progressPercent}%
            </span>
          </div>
          
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <CaretRight size={18} className={isDark ? 'text-white/50' : 'text-gray-400'} />
          </motion.div>
        </div>
      </div>

      {/* Checklist Items */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className={`px-4 pb-4 space-y-2 ${isDark ? 'border-t border-white/[0.06]' : 'border-t border-violet-100'} pt-3`}>
              {checklistItems.map((item, index) => {
                const isItemComplete = progress[item.id];
                
                return (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isItemComplete
                        ? isDark 
                          ? 'bg-emerald-500/10' 
                          : 'bg-emerald-50'
                        : isDark 
                          ? 'bg-white/[0.03] hover:bg-white/[0.06]' 
                          : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    {/* Checkbox */}
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isItemComplete
                        ? 'bg-emerald-500'
                        : isDark 
                          ? 'border-2 border-white/20' 
                          : 'border-2 border-gray-300'
                    }`}>
                      {isItemComplete && (
                        <Check size={14} weight="bold" className="text-white" />
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${
                        isItemComplete
                          ? isDark ? 'text-emerald-400' : 'text-emerald-600'
                          : isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {item.title}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                        {item.description}
                      </p>
                    </div>
                    
                    {/* Action */}
                    {!isItemComplete && (
                      <Link
                        to={item.path}
                        onClick={() => {
                          // Pour view_dashboard, marquer comme complet immédiatement
                          if (item.id === 'view_dashboard') {
                            markAsComplete(item.id);
                            // Track dashboard visit
                            axios.post(`${BACKEND_URL}/api/onboarding/track-visit`, 
                              { section: 'stats' }, 
                              { withCredentials: true }
                            ).catch(() => {});
                          }
                        }}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gradient-to-r from-violet-500 to-pink-500 text-white hover:opacity-90 transition-opacity flex-shrink-0"
                        data-testid={`onboarding-cta-${item.id}`}
                      >
                        {item.cta}
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
            
            {isComplete && (
              <div className={`px-4 pb-4 ${isDark ? 'border-t border-white/[0.06]' : 'border-t border-violet-100'} pt-3`}>
                <button
                  onClick={() => setIsExpanded(false)}
                  className={`w-full py-2 text-sm font-medium rounded-lg transition-colors ${
                    isDark 
                      ? 'bg-white/[0.06] hover:bg-white/[0.1] text-white/70' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                  }`}
                >
                  Masquer la checklist
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
